import React, { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';
import './FaceRegistration.css';

const FaceRegistration = ({ student, onComplete }) => {
  const [step, setStep] = useState('instructions');
  const [message, setMessage] = useState('Preparing face registration...');
  const [faceImage, setFaceImage] = useState(null);
  const webcamRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [captureProgress, setCaptureProgress] = useState(0);
  const [facePosition, setFacePosition] = useState('center');
  const [faceData, setFaceData] = useState({
    center: null,
    left: null,
    right: null
  });
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceDistance, setFaceDistance] = useState(null);
  const [lightingCondition, setLightingCondition] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [browserSupport, setBrowserSupport] = useState({
    mediaDevices: false,
    getUserMedia: false,
    cameraSupport: false,
    isSecureContext: false
  });
  const [orientation, setOrientation] = useState(window.orientation || 0);

  // File upload handler
  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      
      reader.onload = (event) => {
        const imageSrc = event.target.result;
        setFaceImage(imageSrc);
        setFaceData({
          center: imageSrc,
          left: imageSrc,
          right: imageSrc
        });
        
        setStep('processing');
        setMessage('Processing uploaded photo...');
        
        setTimeout(() => {
          const faceDataPayload = {
            images: {
              center: imageSrc,
              left: imageSrc,
              right: imageSrc
            },
            timestamp: new Date().toISOString(),
            source: 'upload'
          };
          
          setStep('complete');
          setMessage('Registration complete with uploaded photo!');
          
          setTimeout(() => {
            onComplete(JSON.stringify(faceDataPayload));
          }, 1500);
        }, 2000);
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Check browser support on component mount
  useEffect(() => {
    const checkSupport = () => {
      const mediaDevices = !!navigator.mediaDevices;
      const getUserMedia = mediaDevices && !!navigator.mediaDevices.getUserMedia;
      const isSecureContext = window.isSecureContext;
      
      // Check for legacy browser support
      const legacyGetUserMedia = 
        !!navigator.getUserMedia || 
        !!navigator.webkitGetUserMedia || 
        !!navigator.mozGetUserMedia || 
        !!navigator.msGetUserMedia;

      setBrowserSupport({
        mediaDevices,
        getUserMedia: getUserMedia || legacyGetUserMedia,
        cameraSupport: (getUserMedia || legacyGetUserMedia) && isSecureContext,
        isSecureContext
      });
    };
    
    checkSupport();

    // Handle orientation changes
    const handleOrientationChange = () => {
      setOrientation(window.orientation || 0);
      // Reinitialize camera on orientation change if needed
      if (step === 'positioning' || step === 'capturing') {
        handleStartScan();
      }
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [step]);

  // Universal camera access handler with better mobile support
  const getCameraAccess = useCallback(async (constraints) => {
    try {
      // Standard modern browsers
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        return await navigator.mediaDevices.getUserMedia(constraints);
      }
      
      // Legacy browsers
      if (navigator.getUserMedia) {
        return new Promise((resolve, reject) => {
          navigator.getUserMedia(constraints, resolve, reject);
        });
      }
      if (navigator.webkitGetUserMedia) {
        return new Promise((resolve, reject) => {
          navigator.webkitGetUserMedia(constraints, resolve, reject);
        });
      }
      if (navigator.mozGetUserMedia) {
        return new Promise((resolve, reject) => {
          navigator.mozGetUserMedia(constraints, resolve, reject);
        });
      }
      
      throw new Error('Camera API not available');
    } catch (err) {
      console.error('Camera access error:', err.name, err.message);
      throw err;
    }
  }, []);

  const handleStartScan = async () => {
    try {
      if (!browserSupport.cameraSupport) {
        let errorMsg = 'Camera not supported in this browser. ';
        if (!browserSupport.isSecureContext) {
          errorMsg += 'Camera access requires HTTPS connection.';
        }
        throw new Error(errorMsg);
      }

      setMessage('Requesting camera access...');
      
      // Mobile-friendly constraints
      const constraints = {
        video: {
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 },
          facingMode: 'user',
          frameRate: { ideal: 24 }
        }
      };

      // Handle iOS specific requirements
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        constraints.video = {
          facingMode: 'user',
          width: { ideal: window.screen.width },
          height: { ideal: window.screen.height }
        };
      }

      const stream = await getCameraAccess(constraints);
      setHasPermission(true);
      
      // Store stream reference safely
      if (webcamRef.current) {
        webcamRef.current.stream = stream;
      }
      
      setStep('positioning');
      setMessage('Please position your face in the frame');
      setCaptureProgress(0);
    } catch (err) {
      console.error('Camera access error:', err);
      setHasPermission(false);
      
      let errorMessage = 'Camera access not available. ';
      if (err.message.includes('permission')) {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (err.message.includes('HTTPS')) {
        errorMessage += 'Please use HTTPS connection for camera access.';
      } else if (err.message.includes('not supported')) {
        errorMessage += 'Please try Chrome, Firefox, or Safari.';
      } else if (err.message.includes('not available')) {
        errorMessage += 'Your device may not have a camera or it may be in use by another app.';
      }
      
      setMessage(errorMessage);
    }
  };

  // Face capture function with proper dependencies
  const captureFace = useCallback(async (position) => {
    if (!faceDetected) {
      setMessage('Face not detected. Please position your face properly.');
      return false;
    }

    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) throw new Error('Failed to capture image');
      
      setFaceData(prev => ({ ...prev, [position]: imageSrc }));
      
      // Update progress
      if (position === 'center') setCaptureProgress(33);
      if (position === 'left') setCaptureProgress(66);
      if (position === 'right') setCaptureProgress(100);
      
      return true;
    } catch (error) {
      console.error('Capture error:', error);
      setMessage(`Failed to capture ${position} view. Please try again.`);
      return false;
    }
  }, [faceDetected]);

  // Countdown handler with proper cleanup
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      captureFace(facePosition);
      setCountdown(null);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, facePosition, captureFace]);

  // Face detection simulation
  useEffect(() => {
    let interval;
    if ((step === 'positioning' || step === 'capturing') && hasPermission) {
      interval = setInterval(() => {
        if (webcamRef.current) {
          const imageSrc = webcamRef.current.getScreenshot();
          setCurrentImage(imageSrc);
          
          // Simulate face detection
          const randomFace = Math.random() > 0.3;
          setFaceDetected(randomFace);
          
          if (randomFace) {
            setFaceDistance(Math.random() > 0.5 ? 'good' : Math.random() > 0.5 ? 'too close' : 'too far');
            setLightingCondition(Math.random() > 0.7 ? 'low light' : 'good');
          } else {
            setFaceDistance(null);
            setLightingCondition(null);
          }
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, hasPermission]);

  // Camera cleanup with proper ref handling
  useEffect(() => {
    const currentRef = webcamRef.current;
    return () => {
      if (currentRef && currentRef.stream) {
        currentRef.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCaptureProcess = async () => {
    if (!faceDetected) {
      setMessage('Please position your face in the frame to begin');
      return;
    }
    setStep('capturing');
    setCaptureProgress(0);
    await captureFaceSequence();
  };

  const captureFaceSequence = useCallback(async () => {
    // Center capture
    setFacePosition('center');
    setMessage('Looking straight ahead...');
    setCountdown(3);
    await new Promise(resolve => {
      const check = () => {
        if (countdown === null) resolve();
        else setTimeout(check, 100);
      };
      check();
    });
    
    // Left capture
    setFacePosition('left');
    setMessage('Slowly turn your head to the left...');
    setCountdown(3);
    await new Promise(resolve => {
      const check = () => {
        if (countdown === null) resolve();
        else setTimeout(check, 100);
      };
      check();
    });
    
    // Right capture
    setFacePosition('right');
    setMessage('Now turn your head to the right...');
    setCountdown(3);
    await new Promise(resolve => {
      const check = () => {
        if (countdown === null) resolve();
        else setTimeout(check, 100);
      };
      check();
    });
    
    // Complete
    setStep('processing');
    setMessage('Processing your facial data...');
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const faceDataPayload = {
      images: faceData,
      timestamp: new Date().toISOString(),
      deviceInfo: {
        isMobile: /Mobi|Android/i.test(navigator.userAgent),
        browser: detectBrowser(),
        orientation
      }
    };
    
    setFaceImage(faceData.center);
    setStep('complete');
    setMessage('Face registration complete!');
    await new Promise(resolve => setTimeout(resolve, 1500));
    onComplete(JSON.stringify(faceDataPayload));
  }, [faceData, countdown, onComplete, orientation]);

  // Browser detection helper
  const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
    return 'Unknown';
  };

  const getPositioningStyle = () => {
    if (facePosition === 'left') return { transform: 'translateX(-30px) rotate(-15deg)' };
    if (facePosition === 'right') return { transform: 'translateX(30px) rotate(15deg)' };
    return {};
  };

  const getFeedbackMessage = () => {
    if (!faceDetected) return 'Position your face in the frame';
    if (faceDistance === 'too close') return 'Move slightly further back';
    if (faceDistance === 'too far') return 'Move slightly closer';
    if (lightingCondition === 'low light') return 'More lighting needed';
    return 'Hold still...';
  };

  return (
    <div className="face-registration-container">
      <h2>Register Face ID</h2>
      
      {!browserSupport.cameraSupport ? (
        <div className="browser-not-supported">
          <h3>Browser Not Supported</h3>
          <p>Your browser doesn't support camera access. Please try one of these options:</p>
          <ul>
            <li>Use Chrome, Firefox, or Safari on desktop</li>
            <li>Use Chrome or Safari on iOS devices</li>
            <li>Use Chrome on Android devices</li>
            <li>Ensure you're on HTTPS (or localhost)</li>
          </ul>
          <div className="fallback-option">
            <label className="upload-button">
              Upload Photos Instead
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                multiple
              />
            </label>
          </div>
        </div>
      ) : step === 'instructions' ? (
        <div className="instructions-step">
          <div className="biometric-icon">
            <svg width="80" height="80" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="#911245" />
            </svg>
          </div>
          <p className="instructions-text">
            We need to capture your face from multiple angles for secure verification.
          </p>
          <div className="requirements-list">
            <p>Please ensure:</p>
            <ul>
              <li>Good lighting on your face</li>
              <li>No hats or sunglasses</li>
              <li>Face the camera directly</li>
            </ul>
          </div>
          <button onClick={handleStartScan} className="begin-scan-button">
            Begin Face Scan
          </button>
        </div>
      ) : step === 'positioning' || step === 'capturing' ? (
        <div className="scanning-step">
          <div className="camera-container">
            {hasPermission ? (
              <>
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    width: { min: 640, ideal: 1280, max: 1920 },
                    height: { min: 480, ideal: 720, max: 1080 },
                    facingMode: 'user'
                  }}
                  onUserMediaError={() => {
                    setHasPermission(false);
                    setMessage('Unable to access camera. Please refresh and allow access.');
                  }}
                  className="webcam-view"
                  forceScreenshotSourceSize={true}
                  screenshotQuality={0.8}
                />
                <div className="face-guide-overlay">
                  <div className={`face-outline ${facePosition} ${faceDetected ? 'detected' : ''}`}>
                    {faceDetected && (
                      <div className="face-position-indicator" style={getPositioningStyle()}>
                        <svg width="60" height="60" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="#72930f" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                {currentImage && (
                  <div className="live-preview">
                    <img src={currentImage} alt="Live preview" />
                  </div>
                )}
              </>
            ) : (
              <div className="permission-denied">
                <p className="permission-warning">{message}</p>
                <button onClick={handleStartScan} className="retry-permission-button">
                  Retry Camera Access
                </button>
                <div className="fallback-option">
                  <label className="upload-button">
                    Upload Photos Instead
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      multiple
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
          
          <div className="scan-controls">
            <p className="scan-message">
              {countdown ? `Capturing in ${countdown}...` : message}
              {faceDetected && (
                <span className="feedback-message">{getFeedbackMessage()}</span>
              )}
            </p>
            
            {step === 'positioning' && (
              <button 
                onClick={startCaptureProcess}
                className={`start-capture-button ${!faceDetected ? 'disabled' : ''}`}
                disabled={!faceDetected}
              >
                {faceDetected ? 'Start Capture' : 'Waiting for face detection...'}
              </button>
            )}
            
            {step === 'capturing' && (
              <div className="capture-progress">
                <div className="progress-bar" style={{ width: `${captureProgress}%` }} />
                <span>Progress: {captureProgress}%</span>
              </div>
            )}
          </div>
        </div>
      ) : step === 'processing' ? (
        <div className="processing-step">
          <div className="processing-spinner">
            <svg width="60" height="60" viewBox="0 0 50 50">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#1560bd"
                strokeWidth="5"
                strokeDasharray="90, 150"
                strokeDashoffset="0"
              />
            </svg>
          </div>
          <p className="processing-message">{message}</p>
        </div>
      ) : (
        <div className="complete-step">
          <div className="capture-preview">
            <img src={faceImage} alt="Registered face" className="face-preview" />
            <div className="success-badge">
              <svg width="40" height="40" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#72930f" />
              </svg>
            </div>
          </div>
          <p className="success-message">{message}</p>
        </div>
      )}
    </div>
  );
};

export default FaceRegistration;